import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, RefreshCw, ChevronDown, Plus, MoreHorizontal, MessageSquare, AlertTriangle, CheckCircle2, Star, Sun, Moon, Target, BarChart2, Users, Settings, Bell, Menu, User, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import TimeRangeFilter from '../TimeRangeFilter';
import { useAuth } from '../../contexts/AuthContext';

const StatBadge = ({ icon: Icon, color, value, label }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-background/40 border border-white/5 hover:bg-background/60 transition-all duration-200"
  >
    <div className={cn("h-7 w-7 rounded-full flex items-center justify-center", color)}>
      <Icon className="h-3.5 w-3.5" />
    </div>
    <div className="flex flex-col">
      <span className="text-sm font-medium">{value}</span>
      <span className="text-xs text-muted-foreground/80">{label}</span>
    </div>
  </motion.div>
);

const GoalProgress = ({ current, target, label }) => {
  const progress = Math.min((current / target) * 100, 100);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground/80">{label}</span>
        <span className="font-medium">{current}/{target}</span>
      </div>
      <div className="h-1 w-full bg-background/30 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={cn(
            "h-full rounded-full",
            progress >= 100 ? "bg-emerald-500" : "bg-primary"
          )}
        />
      </div>
    </div>
  );
};

const AppHeader = ({
  title,
  icon: Icon,
  searchPlaceholder,
  onSearch,
  searchValue,
  stats,
  goals,
  onThemeToggle,
  isDarkMode,
  children,
  actions,
  onSettingsClick,
  onMenuClick,
}) => {
  const [showTimeRangeModal, setShowTimeRangeModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="flex-none bg-background/60 backdrop-blur-xl border-b border-border/40 sticky top-0 z-50">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />

        {/* Main Header */}
        <div className="relative flex items-center justify-between gap-3 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4">
          {/* Left: Title + Icon */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-semibold tracking-tight truncate">{title}</h1>
              <p className="text-xs sm:text-sm text-muted-foreground/70 hidden sm:block">Dashboard Overview</p>
            </div>
          </div>

          {/* Center: Search - Hidden on mobile, shown on tablet+ */}
          {searchPlaceholder && (
            <div className="flex-1 max-w-md hidden lg:block mx-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={(e) => onSearch?.(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-background/40 border border-border/40 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-all duration-200 placeholder:text-muted-foreground/50"
                />
              </div>
            </div>
          )}

          {/* Right: Actions */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {children}

            {/* Mobile menu button */}
            {onMenuClick && (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 p-0 lg:hidden text-muted-foreground hover:text-foreground hover:bg-background/40 rounded-xl"
                onClick={onMenuClick}
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}

            {/* Mobile search button */}
            {searchPlaceholder && (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 p-0 lg:hidden text-muted-foreground hover:text-foreground hover:bg-background/40 rounded-xl"
                onClick={() => {
                  // Could trigger a mobile search modal here
                  console.log('Mobile search triggered');
                }}
              >
                <Search className="h-5 w-5" />
              </Button>
            )}

            {/* Mobile TimeRangeFilter - Icon only */}
            <div className="lg:hidden">
              <TimeRangeFilter
                isMobile={true}
                onMobileOpen={() => setShowTimeRangeModal(true)}
              />
            </div>

            {/* Mobile settings button */}
            {onSettingsClick && (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 p-0 lg:hidden text-muted-foreground hover:text-foreground hover:bg-background/40 rounded-xl"
                onClick={onSettingsClick}
              >
                <Settings className="h-5 w-5" />
              </Button>
            )}

            {/* Desktop TimeRangeFilter */}
            <div className="hidden lg:block">
              <TimeRangeFilter />
            </div>

            {/* User Menu - Mobile */}
            <div className="lg:hidden relative">
              <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 p-0 text-muted-foreground hover:text-foreground hover:bg-background/40 rounded-xl"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <User className="h-5 w-5" />
              </Button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-48 bg-background/95 backdrop-blur-sm border border-border/50 rounded-xl shadow-lg py-2 z-50"
                  >
                    <div className="px-4 py-3 border-b border-border/30">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{user?.displayName || 'User'}</p>
                          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="py-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {onThemeToggle && (
              <>
                <div className="h-6 w-px bg-border/40 hidden sm:block" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-10 w-10 sm:h-9 sm:w-9 p-0 text-muted-foreground hover:text-foreground hover:bg-background/40 rounded-xl"
                  onClick={onThemeToggle}
                >
                  {isDarkMode ? (
                    <Sun className="h-5 w-5 sm:h-4 sm:w-4" />
                  ) : (
                    <Moon className="h-5 w-5 sm:h-4 sm:w-4" />
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Backdrop for mobile user menu */}
      <AnimatePresence>
        {showUserMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
            onClick={() => setShowUserMenu(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AppHeader; 